import { Injectable } from '@angular/core';
import { LoggerService } from '../../core/logger.service';
import { ApiService } from '../../core/api.service';
import { SyncCryptService } from '../../core/crypt/sync-crypt.service';
import { DomSanitizer } from '@angular/platform-browser';
import { IPaginate, IPathData } from 'src/sync';
import { TransferService } from 'src/app/transfer/services/transfer.service';
import { BuildTransferItemService } from 'src/app/transfer/services/build-transfer-item.service';
import { FileItem } from 'src/app/shared/models/files.model';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { SetThumbnails } from 'src/app/actions/file-list.actions';
import { SetLinkThumbnails } from 'src/app/actions/link-file-list.actions';

@Injectable({
    providedIn: 'root'
})
export class ThumbnailsService {

    private isRunning: boolean;

    constructor(
        private api: ApiService,
        private crypt: SyncCryptService,
        private log: LoggerService,
        private sanitizer: DomSanitizer,
        private transferService: TransferService,
        private buildTransferItemService: BuildTransferItemService,
        private store: Store<fromRoot.State>,
    ) { }

    public async getThumbs(displayed: Array<FileItem>, paginate: IPaginate, isLinkMode = false) {
        const start = Date.now();
        const list = this.getThumbList(displayed, paginate);
        this.log.d((Date.now() - start) + ' ms to getThumbList');
        // if this list is empty, there's no thumbs to get, return
        if (!list || !list.length) {
            this.log.d('No items to get');
            return;
        }

        this.isRunning = true;
        const items = list.map((curFile) => {
            return {
                cachekey: curFile.cachekey,
                blobtype: 'btTHUMB1',
                maxsize: 262144
            };
        });
        
        const [keyData, response] = await Promise.all([this.buildTransferItemService.getFileKeys(list), this.api.fetchThumbs(items)])
        const manifestStart = response.byteLength - 4096,
            manifest = response.slice(manifestStart),
            thumbs = response.slice(0, manifestStart),
            m = JSON.parse(this.crypt.bytesToString(new Uint8Array(manifest)));

        const updatedThumbnails  = await this.processThumb(list, m, thumbs, displayed, keyData);

        // Dispatch the action once after processing all thumbnails
        if (isLinkMode) {
            this.store.dispatch(new SetLinkThumbnails({ thumbnails: updatedThumbnails }));
        } else {
            this.store.dispatch(new SetThumbnails({ thumbnails: updatedThumbnails }));
        }
    }
    
    public async processThumb(list: FileItem[], manifest: number[][], thumbs: ArrayBuffer, displayed: Array<FileItem>, keyData: IPathData) {
        if (!list.length) {
            return;
        }

        const startTime = Date.now();
        let updatedThumbnails: FileItem[] = [];
        const processingPromises = list.map(async (curItem, index) => {
            const man: Array<number> = manifest[index];
            const start: number = man[0];
            const end: number = start + man[1];
            const thumb = thumbs.slice(start, end);

            if (!curItem || !man || end === 0) {
                return;
            }
            return this.transferService.getThumb(curItem, keyData, thumb);
        });

        const result = await Promise.all(processingPromises);
        result.forEach((tItem, index) => {
            if (tItem.status === 1) {
                const updatedItem = {
                    ...list[index],
                    imgsrc: this.sanitizer.bypassSecurityTrustUrl(tItem.renderFile.url),
                    thumbsrc: tItem.renderFile.url,
                    thumbData: { ...list[index].thumbData, processed: true }
                };
                updatedThumbnails.push(updatedItem);
            }
        });
        this.log.info('Thumbnails finished ' + (Date.now() - startTime) + ' ms');
        this.isRunning = false;
        return updatedThumbnails
    }
    
    public getThumbList(list: Array<FileItem>, paginate: IPaginate): Array<FileItem> {

        const thumbs: Array<FileItem> = [];

        const start = (paginate.currentPage - 1) * paginate.itemsPerPage;
        const end = (start + paginate.itemsPerPage > paginate.total)
            ? paginate.total
            : start + paginate.itemsPerPage;

        for (let i = end - 1; i >= start; i--) {
            let cur = list[i];
            if (!cur || (cur.thumbData && cur.thumbData.processed == true)) { continue; }
            cur = { ...cur };
            if (cur.size > 0 && cur.has_thumb1 == 1) {
                cur.thumbData = {
                    processed: false,
                    thumbType: 1,
                    cachekey: cur.cachekey,
                    blobtype: 'btTHUMB1'
                };
                thumbs.push(cur);
            } else if (cur.kind == 'image' && cur.size > 0 &&
                cur.size < 1024 * 100 && cur.type == 'file' &&
                !cur.has_thumb1 && (cur.filetype && cur.filetype?.viewable)
            ) {
                cur.thumbData = {
                    processed: false,
                    thumbType: 1,
                    cachekey: cur.cachekey,
                    blobtype: 'btFILE'
                };
                thumbs.push(cur);
            }
        }
        return thumbs;
    }

}
